import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Calendar,
    User,
    MapPin,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    DollarSign,
    Mail,
    Phone,
    Edit,
    RefreshCw
} from 'lucide-react';
import { 
    useGetBookingDetailsQuery, 
    useUpdateBookingStatusMutation,
    useProcessRefundMutation
} from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { API_BASE_URL } from '../../config/api';

const BookingDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const bookingId = id ? parseInt(id) : null;
    const isValidId = bookingId && !isNaN(bookingId) && bookingId > 0;

    const { data: bookingResponse, isLoading, error, refetch } = useGetBookingDetailsQuery(bookingId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true
    });
    
    const [updateBookingStatus, { isLoading: isUpdatingStatus }] = useUpdateBookingStatusMutation();
    const [processRefund, { isLoading: isProcessingRefund }] = useProcessRefundMutation();

    const booking = bookingResponse?.data;

    // Helper function to get proper image URL
    const getImageUrl = (imagePath: string) => {
        if (!imagePath) return '';
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        const baseUrl = API_BASE_URL;
        return imagePath.startsWith('/') ? `${baseUrl}${imagePath}` : `${baseUrl}/${imagePath}`;
    };

    // Modal states
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);

    // Status update form state
    const [statusForm, setStatusForm] = useState({
        status: 'pending' as 'pending' | 'confirmed' | 'cancelled' | 'completed',
        cancellation_reason: ''
    });

    // Refund form state
    const [refundForm, setRefundForm] = useState({
        refund_amount: '',
        notes: ''
    });

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed':
                return 'bg-green-100 text-green-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
                return 'bg-red-100 text-red-800';
            case 'completed':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    // Get status icon
    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'confirmed':
                return <CheckCircle className="h-5 w-5 text-green-600" />;
            case 'pending':
                return <Clock className="h-5 w-5 text-yellow-600" />;
            case 'cancelled':
                return <XCircle className="h-5 w-5 text-red-600" />;
            case 'completed':
                return <CheckCircle className="h-5 w-5 text-blue-600" />;
            default:
                return <AlertCircle className="h-5 w-5 text-gray-600" />;
        }
    };

    // Format date
    const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

    // Format currency
    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Handle status update
    const handleUpdateStatus = async () => {
        if (!booking) return;

        try {
            await updateBookingStatus({
                booking_id: booking.booking_id,
                status: statusForm.status,
                cancellation_reason: statusForm.status === 'cancelled' ? statusForm.cancellation_reason || 'Cancelled by admin' : null
            }).unwrap();

            toast.success('Booking status updated successfully!');
            setShowStatusModal(false);
            refetch();
        } catch (error: any) {
            console.error('Failed to update booking status:', error);
            toast.error(error?.data?.message || 'Failed to update booking status');
        }
    };

    // Handle refund
    const handleProcessRefund = async () => {
        if (!booking || !refundForm.refund_amount) return;

        try {
            const refundAmount = parseFloat(refundForm.refund_amount);
            if (isNaN(refundAmount) || refundAmount <= 0) {
                toast.error('Please enter a valid refund amount');
                return;
            }

            await processRefund({
                booking_id: booking.booking_id,
                refund_amount: refundAmount,
                notes: refundForm.notes || null
            }).unwrap();

            toast.success('Refund processed successfully!');
            setShowRefundModal(false);
            setRefundForm({ refund_amount: '', notes: '' });
            refetch();
        } catch (error: any) {
            console.error('Failed to process refund:', error);
            toast.error(error?.data?.message || 'Failed to process refund');
        }
    };

    // Initialize form when booking loads
    React.useEffect(() => {
        if (booking) {
            setStatusForm({
                status: booking.status,
                cancellation_reason: booking.cancellation_reason || ''
            });
        }
    }, [booking]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading booking details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Booking</h2>
                    <p className="text-gray-600 mb-6">
                        {(error as any)?.data?.message || 'There was an error loading the booking details. Please try again.'}
                    </p>
                    <div className="space-x-4">
                        <button
                            onClick={() => refetch()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                        <button
                            onClick={() => navigate('/admin/bookings')}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            ← Back to Bookings
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!booking) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Booking Not Found</h2>
                    <p className="text-gray-600 mb-6">The booking you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/admin/bookings')}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        ← Back to Bookings
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/bookings')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Booking Details</h1>
                                <p className="text-gray-600">Reference: {booking.booking_reference}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => refetch()}
                                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
                            >
                                <RefreshCw className="h-4 w-4" />
                                <span>Refresh</span>
                            </button>
                            <button
                                onClick={() => setShowStatusModal(true)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                            >
                                <Edit className="h-4 w-4" />
                                <span>Update Status</span>
                            </button>
                            {booking.status === 'cancelled' && !booking.is_refunded && (
                                <button
                                    onClick={() => setShowRefundModal(true)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                                >
                                    <DollarSign className="h-4 w-4" />
                                    <span>Process Refund</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Booking Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 bg-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        {getStatusIcon(booking.status)}
                                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                                            {booking.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-gray-600">Booking ID</p>
                                        <p className="text-lg font-bold text-gray-900">#{booking.booking_id}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Information</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Calendar className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Check-in Date</p>
                                            <p className="text-base font-medium text-gray-900">{formatDate(booking.check_in_date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <Calendar className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Check-out Date</p>
                                            <p className="text-base font-medium text-gray-900">{formatDate(booking.check_out_date)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <User className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Number of Guests</p>
                                            <p className="text-base font-medium text-gray-900">{booking.guests}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <DollarSign className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Total Amount</p>
                                            <p className="text-base font-medium text-gray-900">
                                                {booking.total_amount 
                                                    ? formatCurrency(booking.total_amount)
                                                    : booking.amount
                                                    ? formatCurrency(booking.amount)
                                                    : booking.membership
                                                    ? 'Included in Membership'
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                {booking.cancellation_reason && (
                                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <div className="flex items-start space-x-2">
                                            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-red-900">Cancellation Reason</p>
                                                <p className="text-sm text-red-700 mt-1">{booking.cancellation_reason}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* User Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">User Information</h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <User className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Name</p>
                                            <p className="text-base font-medium text-gray-900">{booking.user?.name || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-green-100 rounded-lg">
                                            <Mail className="h-5 w-5 text-green-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Email</p>
                                            <p className="text-base font-medium text-gray-900">{booking.user?.email || 'N/A'}</p>
                                        </div>
                                    </div>
                                    {booking.user?.mobile_number && (
                                        <div className="flex items-start space-x-3">
                                            <div className="p-2 bg-green-100 rounded-lg">
                                                <Phone className="h-5 w-5 text-green-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Mobile Number</p>
                                                <p className="text-base font-medium text-gray-900">{booking.user.mobile_number}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Apartment Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Apartment Information</h2>
                            </div>
                            <div className="p-6">
                                {booking.apartment && (
                                    <>
                                        <div className="flex items-center mb-6">
                                            {booking.apartment.images && booking.apartment.images.length > 0 && (
                                                <img
                                                    src={getImageUrl(booking.apartment.images[0])}
                                                    alt={booking.apartment.name}
                                                    className="w-24 h-24 object-cover rounded-xl mr-4 border border-gray-200 shadow-sm"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = 'https://via.placeholder.com/200?text=No+Image';
                                                        target.className = 'w-24 h-24 object-contain rounded-xl mr-4 border border-gray-200 bg-gray-50 p-2';
                                                    }}
                                                />
                                            )}
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900">{booking.apartment.name}</h3>
                                                <p className="text-gray-600">{booking.apartment.location}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Bedrooms and Bathrooms removed - not available in apartment object */}
                                            {booking.apartment.address && (
                                                <div className="flex items-start space-x-3 md:col-span-2">
                                                    <div className="p-2 bg-purple-100 rounded-lg">
                                                        <MapPin className="h-5 w-5 text-purple-600" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-sm text-gray-600">Address</p>
                                                        <p className="text-base font-medium text-gray-900">{booking.apartment.address}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {booking.apartment.images && booking.apartment.images.length > 0 && (
                                            <div className="mt-6">
                                                <h4 className="text-lg font-semibold text-gray-900 mb-4">Gallery</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                    {booking.apartment.images.map((img: string, index: number) => (
                                                        <div key={index} className="relative group overflow-hidden">
                                                            <img
                                                                src={getImageUrl(img)}
                                                                alt={`Apartment image ${index + 1}`}
                                                                className="w-full h-48 md:h-56 object-cover rounded-lg border border-gray-200 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer"
                                                                onError={(e) => {
                                                                    const target = e.target as HTMLImageElement;
                                                                    target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                                                    target.className = 'w-full h-48 md:h-56 object-contain rounded-lg border border-gray-200 bg-gray-50 p-4';
                                                                }}
                                                                loading="lazy"
                                                            />
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all duration-300"></div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Refund Information */}
                        {booking.is_refunded && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                                    <h2 className="text-lg font-semibold text-green-900">Refund Information</h2>
                                </div>
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-sm text-gray-600">Refund Amount</span>
                                        <span className="text-xl font-bold text-green-600">
                                            {booking.refund_amount ? formatCurrency(booking.refund_amount) : 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className="h-5 w-5 text-green-600" />
                                        <span className="text-sm font-medium text-green-900">Refund Processed</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Timeline */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Timeline</h2>
                            </div>
                            <div className="p-6">
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <div className="p-2 bg-blue-100 rounded-full">
                                            <Clock className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">Booking Created</p>
                                            <p className="text-xs text-gray-600">
                                                {formatDateTimeUtil(booking.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                    {booking.updated_at && booking.updated_at !== booking.created_at && (
                                        <div className="flex items-start space-x-3">
                                            <div className="p-2 bg-green-100 rounded-full">
                                                <Clock className="h-4 w-4 text-green-600" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900">Last Updated</p>
                                                <p className="text-xs text-gray-600">
                                                    {formatDateTimeUtil(booking.updated_at)}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Status Update Modal */}
            <Modal
                isOpen={showStatusModal}
                onClose={() => {
                    setShowStatusModal(false);
                }}
                title="Update Booking Status"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                        <select
                            value={statusForm.status}
                            onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value as any })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>

                    {statusForm.status === 'cancelled' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Reason</label>
                            <textarea
                                value={statusForm.cancellation_reason}
                                onChange={(e) => setStatusForm({ ...statusForm, cancellation_reason: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows={3}
                                placeholder="Enter cancellation reason (optional)"
                            />
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => setShowStatusModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleUpdateStatus}
                            disabled={isUpdatingStatus}
                        >
                            {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Refund Modal */}
            <Modal
                isOpen={showRefundModal}
                onClose={() => {
                    setShowRefundModal(false);
                    setRefundForm({ refund_amount: '', notes: '' });
                }}
                title="Process Refund"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount ($) *</label>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={refundForm.refund_amount}
                            onChange={(e) => setRefundForm({ ...refundForm, refund_amount: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter refund amount"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                        <textarea
                            value={refundForm.notes}
                            onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                            placeholder="Enter notes for this refund"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowRefundModal(false);
                                setRefundForm({ refund_amount: '', notes: '' });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleProcessRefund}
                            disabled={isProcessingRefund || !refundForm.refund_amount}
                        >
                            {isProcessingRefund ? 'Processing...' : 'Process Refund'}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default BookingDetails;

